import type { GameIntent, EncounterState, Vec3 } from "../types/game";
import type { SceneCommandExecutor } from "./SceneCommandExecutor";
import type { IntentResolver } from "./IntentResolver";
import { COMBAT } from "./config";

// Faction → default weapon type
const FACTION_WEAPONS: Record<string, string> = {
  "Iron Corsairs": "disruptor",
  "Velan Dominion": "disruptor",
  "Stellar Coalition": "phaser",
};

const SEVERITY_LABELS = ["light", "moderate", "heavy", "critical"] as const;

function dist(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

export class CombatSimulator {
  private fireCooldowns = new Map<string, number>();
  private llmCooldownReset = new Set<string>();
  // Phase 4.3: combat is "open" once a player or NPC has actually fired in this
  // encounter. Until then the simulator does NOT auto-fire — even if escalation
  // says "combat", we don't shoot at the player unprompted. Player primacy.
  private combatOpen = false;
  private getResolver: () => IntentResolver | null;
  private getEncounterState: () => EncounterState | null;
  private getExecutor: () => SceneCommandExecutor | null;

  constructor(
    getResolver: () => IntentResolver | null,
    getEncounterState: () => EncounterState | null,
    getExecutor: () => SceneCommandExecutor | null,
  ) {
    this.getResolver = getResolver;
    this.getEncounterState = getEncounterState;
    this.getExecutor = getExecutor;
  }

  /** Call when the LLM generates an npc_fire intent to prevent double-fire. */
  registerLLMFire(sourceId: string) {
    const cooldown = COMBAT.FIRE_COOLDOWN_MIN + Math.random() * (COMBAT.FIRE_COOLDOWN_MAX - COMBAT.FIRE_COOLDOWN_MIN);
    this.fireCooldowns.set(sourceId, performance.now() / 1000 + cooldown);
    this.llmCooldownReset.add(sourceId);
    this.combatOpen = true;
  }

  /** Call when the player fires weapons — opens combat for auto-fire. */
  registerPlayerFire() {
    this.combatOpen = true;
  }

  tick(_deltaSeconds: number) {
    const encounter = this.getEncounterState();
    const executor = this.getExecutor();
    const resolver = this.getResolver();
    if (!encounter || !executor || !resolver) return;

    // Only fire in combat/confrontational escalation AND only after combat
    // has been "opened" by an actual fire event. No unprovoked auto-fire.
    const esc = encounter.escalation;
    if (esc !== "combat" && esc !== "confrontational") return;
    if (!this.combatOpen) return;

    const spatial = executor.currentSpatialState;
    const playerPos = spatial.playerShip.position;
    const now = performance.now() / 1000;

    // Check if player is doing evasive maneuvers (has active evasive movement)
    const isEvasive = spatial.playerShip.speed === "all_stop"; // simplified — TODO: check movement type

    // Find hostile parties
    const hostilePartyIds = new Set(
      encounter.parties
        ?.filter((p) => p.disposition === "hostile")
        .map((p) => p.id) ?? []
    );

    for (const entity of spatial.entities) {
      if (entity.type !== "ship") continue;
      if (entity.cloaked) continue;
      if (entity.condition === "destroyed") continue;

      // Check if this entity's party is hostile
      if (!hostilePartyIds.has(entity.id)) continue;

      // Range check
      const range = dist(entity.position, playerPos);
      if (range > COMBAT.WEAPONS_RANGE) continue;

      // Cooldown check
      const nextFire = this.fireCooldowns.get(entity.id) ?? 0;
      if (now < nextFire) continue;

      // LLM already handled this entity this tick
      if (this.llmCooldownReset.has(entity.id)) continue;

      // FIRE
      const weaponType = FACTION_WEAPONS[entity.faction ?? ""] ?? "disruptor";
      const hitChance = isEvasive ? COMBAT.HIT_CHANCE_EVASIVE : COMBAT.HIT_CHANCE_BASE;
      const hit = Math.random() < hitChance;

      // Roll severity
      const roll = Math.random();
      let severity: typeof SEVERITY_LABELS[number] = "light";
      for (let i = 0; i < COMBAT.SEVERITY_THRESHOLDS.length; i++) {
        if (roll < COMBAT.SEVERITY_THRESHOLDS[i]) { severity = SEVERITY_LABELS[i]; break; }
      }

      // Use torpedo occasionally (20% chance)
      const useTorpedo = Math.random() < 0.2;
      const actualWeapon = useTorpedo ? "photon" : weaponType;

      const intent: GameIntent = {
        type: "npc_fire",
        source: entity.id,
        target: "player_ship",
        weaponType: actualWeapon as any,
        weaponCount: useTorpedo ? 1 : undefined,
        hit,
        severity: hit ? severity : undefined,
      };

      resolver.resolve([intent]);

      // Set cooldown
      const cooldown = COMBAT.FIRE_COOLDOWN_MIN + Math.random() * (COMBAT.FIRE_COOLDOWN_MAX - COMBAT.FIRE_COOLDOWN_MIN);
      this.fireCooldowns.set(entity.id, now + cooldown);
    }

    // Clear LLM reset set each tick
    this.llmCooldownReset.clear();
  }
}
